import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useVideoEditor } from './context/VideoEditorContext';
import { ArrowPathIcon, PencilSquareIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { videoClipApi } from '../../services/user/videoclip.api';
import GlobalLoading from '../GlobalLoading';

export default function VideoClipEditor() {
  const { state, actions } = useVideoEditor();
  const { selectedClip, editConfig } = state;
  const { t } = useTranslation(['editor', 'common']);

  const videoRef = useRef<HTMLVideoElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);

  const [duration, setDuration] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [tempTrimStart, setTempTrimStart] = useState(0);
  const [tempTrimEnd, setTempTrimEnd] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  const clipEditInfo = editConfig?.clips.find(c => c.clipId === selectedClip?.id);
  const currentTrimStart = clipEditInfo?.trimStart || 0;
  const currentTrimEnd = clipEditInfo?.trimEnd || duration;

  const displayTrimStart = isEditing ? tempTrimStart : currentTrimStart;
  const displayTrimEnd = isEditing ? tempTrimEnd : currentTrimEnd;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const handleLoadedMetadata = () => setDuration(video.duration);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    return () => video.removeEventListener('loadedmetadata', handleLoadedMetadata);
  }, [selectedClip?.videoUrl]);

  // 当选中的clip变化时，重置编辑状态
  // 注意：不要在 editConfig 更新时重置，因为保存裁切时会更新 editConfig
  useEffect(() => {
    if (isEditing) {
      setIsEditing(false);
    }
  }, [selectedClip?.id]);

  const startEditing = () => {
    // 进入裁剪模式时暂停播放
    if (state.isPlaying) {
      actions.pause();
    }
    setIsEditing(true);
    setTempTrimStart(currentTrimStart);
    setTempTrimEnd(currentTrimEnd);
  };

  const cancelEditing = () => {
    setIsEditing(false);
  };

  const applyTrimChanges = async () => {
    if (!editConfig || !clipEditInfo || !selectedClip) return;

    const newDuration = tempTrimEnd - tempTrimStart;

    const updatedClips = editConfig.clips.map(c => {
      if (c.clipId === selectedClip.id) {
        return { ...c, trimStart: tempTrimStart, trimEnd: tempTrimEnd, duration: newDuration };
      }
      return c;
    });

    const currentClipIndex = updatedClips.findIndex(c => c.clipId === selectedClip.id);
    for (let i = currentClipIndex + 1; i < updatedClips.length; i++) {
      updatedClips[i] = { ...updatedClips[i], startTime: updatedClips[i - 1].startTime + updatedClips[i - 1].duration };
    }

    const totalDuration = updatedClips.reduce((sum, clip) => sum + clip.duration, 0);
    const newConfig = {
      ...editConfig,
      clips: updatedClips,
      timeline: { ...editConfig.timeline, totalDuration }
    };

    // 立即保存到后台，显示loading
    setIsSaving(true);
    try {
      await actions.saveEditConfigImmediately(newConfig);
      // 同步更新 clip 的 duration 到数据库
      await videoClipApi.updateVideoClip(selectedClip.id, { duration: newDuration });
    } finally {
      setIsSaving(false);
    }

    // 强制更新预览视频位置到裁切的开始位置
    // 这样可以确保视频预览立即反映新的裁切设置
    actions.setPreviewTime(tempTrimStart);

    setIsEditing(false);
  };

  const handleReset = () => {
    setTempTrimStart(0);
    setTempTrimEnd(duration);
  };

  const updateTrimPosition = (type: 'start' | 'end', time: number) => {
    if (type === 'start' && time < tempTrimEnd - 0.1) {
      setTempTrimStart(time);
    } else if (type === 'end' && time > tempTrimStart + 0.1) {
      setTempTrimEnd(time);
    }
  };

  const handleHandleDrag = (type: 'start' | 'end', e: React.MouseEvent) => {
    if (!isEditing) return;
    e.preventDefault();
    e.stopPropagation();

    const handleMouseMove = (e: MouseEvent) => {
      if (!timelineRef.current) return;
      const rect = timelineRef.current.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      const time = Math.max(0, Math.min(duration, percent * duration));
      updateTrimPosition(type, time);

      // 拖动时实时更新预览视频位置
      actions.setPreviewTime(time);
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  if (!selectedClip?.videoUrl) return null;

  const finalDuration = displayTrimEnd - displayTrimStart;

  return (
    <div className="flex items-center justify-center">
      <video ref={videoRef} src={selectedClip.videoUrl} className="hidden" preload="metadata" />
      <div className="w-full max-w-4xl bg-gray-800/50 backdrop-blur-sm rounded-lg px-6 py-3">
        <div className="flex items-center gap-4">
          {/* 时长标签 */}
          <span className="text-sm text-gray-400 whitespace-nowrap min-w-[60px]">
            {finalDuration.toFixed(1)}{t('editor:seconds')}
          </span>

          <div className="flex-1">
            <div ref={timelineRef} className="relative h-2 bg-gray-700 rounded-full cursor-pointer">
              <div
                className={`absolute top-0 h-full rounded-full transition-all ${isEditing ? 'bg-blue-500/80' : 'bg-gray-500/80'}`}
                style={{
                  left: `${(displayTrimStart / duration) * 100}%`,
                  width: `${((displayTrimEnd - displayTrimStart) / duration) * 100}%`
                }}
              />
              <div
                className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full border-2 shadow-lg z-10 transition-all ${isEditing ? 'border-blue-500 cursor-grab active:cursor-grabbing hover:scale-110' : 'border-gray-500 cursor-not-allowed opacity-50'
                  }`}
                style={{ left: `${(displayTrimStart / duration) * 100}%`, transform: 'translate(-50%, -50%)' }}
                onMouseDown={(e) => handleHandleDrag('start', e)}
              />
              <div
                className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full border-2 shadow-lg z-10 transition-all ${isEditing ? 'border-blue-500 cursor-grab active:cursor-grabbing hover:scale-110' : 'border-gray-500 cursor-not-allowed opacity-50'
                  }`}
                style={{ left: `${(displayTrimEnd / duration) * 100}%`, transform: 'translate(-50%, -50%)' }}
                onMouseDown={(e) => handleHandleDrag('end', e)}
              />
            </div>
          </div>
          {!isEditing ? (
            <button onClick={startEditing} title={t('editor:editTrim')} className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors">
              <PencilSquareIcon className="w-5 h-5" />
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button onClick={handleReset} title={t('common:reset')} className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors">
                <ArrowPathIcon className="w-5 h-5" />
              </button>
              <button onClick={cancelEditing} title={t('common:cancel')} className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors">
                <XMarkIcon className="w-5 h-5" />
              </button>
              <button onClick={applyTrimChanges} title={t('common:confirm')} className="p-2 text-blue-400 hover:text-blue-300 hover:bg-gray-700 rounded-lg transition-colors">
                <CheckIcon className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </div>
      <GlobalLoading isOpen={isSaving} message={t('editor:savingTrim')} />
    </div>
  );
}
